const SLA_TZ = "Africa/Cairo";
const JS_DAY_TO_WEEKDAY = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

function cairoDateKey(instant) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: SLA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const map = Object.fromEntries(
    fmt
      .formatToParts(instant)
      .filter((p) => p.type !== "literal")
      .map((p) => [p.type, p.value]),
  );
  return `${map.year}-${map.month}-${map.day}`;
}

function cairoDateUtc(instant) {
  const [y, m, d] = cairoDateKey(instant).split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(
      Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()),
    );
  }
  const iso = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function normalizeSlaWeekDay(value) {
  if (!value) return null;
  const upper = String(value).toUpperCase();
  return JS_DAY_TO_WEEKDAY.includes(upper) ? upper : null;
}

export function startOfNextCairoDay(instant) {
  const today = cairoDateKey(instant);
  let lo = instant.getTime();
  let hi = lo + 36 * 60 * 60 * 1000;
  while (cairoDateKey(new Date(hi)) === today) {
    hi += 12 * 60 * 60 * 1000;
  }
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (cairoDateKey(new Date(mid)) === today) lo = mid + 1;
    else hi = mid;
  }
  return new Date(hi);
}

export function isApprovalSlaOffDay(instant, hold = {}) {
  const rest = normalizeSlaWeekDay(hold.weeklyRestDay);
  const dayUtc = cairoDateUtc(instant);
  const weekday = JS_DAY_TO_WEEKDAY[new Date(dayUtc).getUTCDay()];
  if (rest && weekday === rest) return true;
  return (hold.leaveRanges || []).some((range) => {
    const start = parseDateOnly(range.startDate);
    const end = parseDateOnly(range.endDate);
    if (!start || !end) return false;
    return start.getTime() <= dayUtc && dayUtc <= end.getTime();
  });
}

export function getApprovalSlaProgress(
  stepEnteredAt,
  slaHours,
  hold = {},
  now = new Date(),
) {
  const needed = Math.max(0, slaHours) * 60 * 60 * 1000;
  let elapsed = 0;
  let cursor = stepEnteredAt.getTime();
  const end = now.getTime();
  const cap = end + 400 * 24 * 60 * 60 * 1000;

  while (cursor < end && elapsed < needed) {
    const at = new Date(cursor);
    const next = Math.min(startOfNextCairoDay(at).getTime(), end);
    if (!isApprovalSlaOffDay(at, hold)) {
      elapsed += Math.max(0, next - cursor);
    }
    cursor = next;
  }

  const remainingMs = Math.max(0, needed - elapsed);
  const paused = remainingMs > 0 && isApprovalSlaOffDay(now, hold);

  if (remainingMs === 0) {
    return { remainingMs: 0, paused: false, deadline: now };
  }

  let dlCursor = end;
  let need = remainingMs;
  while (need > 0 && dlCursor < cap) {
    const at = new Date(dlCursor);
    const next = startOfNextCairoDay(at).getTime();
    if (isApprovalSlaOffDay(at, hold)) {
      dlCursor = next;
      continue;
    }
    const take = Math.min(need, Math.max(0, next - dlCursor));
    need -= take;
    dlCursor += take;
  }

  return { remainingMs, paused, deadline: new Date(dlCursor) };
}

export function buildViewerSlaHold(user, leaveRanges = []) {
  return {
    weeklyRestDay: normalizeSlaWeekDay(user?.weeklyRestDay) || "FRIDAY",
    leaveRanges: leaveRanges.filter(
      (range) => range?.startDate && range?.endDate,
    ),
  };
}
